<template>
  <div class="rr-page">
    <div class="rr-toolbar">
      <div class="rr-toolbar__inner">
        <v-select
          v-model="filters.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          label="Partner"
          outlined
          dense
          hide-details
          :disabled="loading"
          class="rr-field rr-field--wide"
          @change="loadSchedule"
        />

        <v-select
          v-model="filters.batch"
          :items="batchItems"
          item-text="label"
          item-value="value"
          label="Batch"
          outlined
          dense
          hide-details
          :disabled="loading || !filters.partner_id"
          class="rr-field"
          @change="loadSchedule"
        />

        <v-menu v-model="fromMenu" :close-on-content-click="false" offset-y min-width="auto">
          <template #activator="{ on, attrs }">
            <v-text-field
              :value="filters.from_date"
              label="Collected From"
              prepend-inner-icon="mdi-calendar-start"
              readonly
              outlined
              dense
              hide-details
              :disabled="loading || !filters.partner_id"
              class="rr-field"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="filters.from_date"
            no-title
            @input="(fromMenu = false), loadSchedule()"
          />
        </v-menu>

        <v-menu v-model="toMenu" :close-on-content-click="false" offset-y min-width="auto">
          <template #activator="{ on, attrs }">
            <v-text-field
              :value="filters.to_date"
              label="Collected To"
              prepend-inner-icon="mdi-calendar-end"
              readonly
              outlined
              dense
              hide-details
              :disabled="loading || !filters.partner_id"
              class="rr-field"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="filters.to_date"
            no-title
            @input="(toMenu = false), loadSchedule()"
          />
        </v-menu>

        <v-spacer />

        <v-btn-toggle v-model="view" mandatory dense class="rr-toggle">
          <v-btn small value="months">
            <v-icon small left>mdi-calendar-month-outline</v-icon>
            Months
          </v-btn>
          <v-btn small value="students">
            <v-icon small left>mdi-account-multiple-outline</v-icon>
            Students
          </v-btn>
        </v-btn-toggle>

        <v-btn
          v-if="hasData"
          small
          outlined
          color="primary"
          class="ml-2"
          @click="downloadSchedule"
        >
          <v-icon small left>mdi-download-outline</v-icon>
          Export
        </v-btn>
      </div>
    </div>

    <div v-if="loading" class="rr-state">
      <v-progress-circular indeterminate color="primary" size="40" width="3" />
      <p class="rr-state__text">Spreading the collections…</p>
    </div>

    <v-card v-else-if="!schedule" outlined class="rr-state rr-state--card">
      <v-icon size="44" color="grey lighten-1">mdi-chart-timeline-variant</v-icon>
      <h3 class="rr-state__title">Pick a partner and a collection window</h3>
      <p class="rr-state__text">
        Each collection is spread over the months it buys — six for a semester,
        twelve for annual, twenty four for a full term — and earned month by
        month from April for a January intake, October for a July one.
      </p>
    </v-card>

    <v-card v-else-if="!hasData" outlined class="rr-state rr-state--card">
      <v-icon size="44" color="grey lighten-1">mdi-cash-remove</v-icon>
      <h3 class="rr-state__title">Nothing collected in this window</h3>
      <p class="rr-state__text">There is no revenue to recognise.</p>
    </v-card>

    <template v-else>
      <!-- Headline -->
      <div class="rr-stats">
        <v-card outlined class="rr-stat">
          <span class="rr-stat__label">Collected</span>
          <span class="rr-stat__value">{{ money(schedule.totals.collection_amount) }}</span>
          <span class="rr-stat__meta">
            {{ schedule.totals.collection_count }} collections
          </span>
        </v-card>
        <v-card outlined class="rr-stat">
          <span class="rr-stat__label">Gross Revenue</span>
          <span class="rr-stat__value">{{ money(schedule.totals.gross_amount) }}</span>
          <span class="rr-stat__meta">over the full service period</span>
        </v-card>
        <v-card outlined class="rr-stat">
          <span class="rr-stat__label">GST</span>
          <span class="rr-stat__value">{{ money(schedule.totals.gst_amount) }}</span>
          <span class="rr-stat__meta">removed from inclusive categories</span>
        </v-card>
        <v-card outlined class="rr-stat rr-stat--accent">
          <span class="rr-stat__label">Net Revenue</span>
          <span class="rr-stat__value">{{ money(schedule.totals.net_amount) }}</span>
          <span class="rr-stat__meta">what the partner earns</span>
        </v-card>
      </div>

      <!-- Financial years -->
      <v-card outlined class="rr-card">
        <div class="rr-card__head">
          <h3 class="rr-card__title">By financial year</h3>
          <p class="rr-card__sub">
            Deferred is what is collected but not yet earned at 31 March.
          </p>
        </div>
        <div class="rr-table-wrap">
          <table class="rr-table">
            <thead>
              <tr>
                <th class="rr-th">Financial Year</th>
                <th class="rr-th rr-th--num">Apportioned Fee</th>
                <th class="rr-th rr-th--num">Gross</th>
                <th class="rr-th rr-th--num">GST</th>
                <th class="rr-th rr-th--num">Net</th>
                <th class="rr-th rr-th--num">Deferred Fee</th>
                <th class="rr-th rr-th--num">Deferred Net</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="fy in schedule.financial_years" :key="fy.financial_year">
                <td class="rr-td rr-td--strong">{{ fy.financial_year }}</td>
                <td class="rr-td rr-td--num">{{ money(fy.apportioned_fee) }}</td>
                <td class="rr-td rr-td--num">{{ money(fy.gross_amount) }}</td>
                <td class="rr-td rr-td--num">{{ money(fy.gst_amount) }}</td>
                <td class="rr-td rr-td--num rr-td--strong">{{ money(fy.net_amount) }}</td>
                <td class="rr-td rr-td--num rr-td--muted">{{ money(fy.deferred_fee) }}</td>
                <td class="rr-td rr-td--num rr-td--muted">{{ money(fy.deferred_net) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>

      <!-- Month by month -->
      <v-card v-if="view === 'months'" outlined class="rr-card">
        <div class="rr-card__head">
          <h3 class="rr-card__title">Month by month</h3>
          <p class="rr-card__sub">
            {{ schedule.months.length }} months from
            {{ schedule.months[0].label }} to
            {{ schedule.months[schedule.months.length - 1].label }}
          </p>
        </div>
        <div class="rr-table-wrap">
          <table class="rr-table">
            <thead>
              <tr>
                <th class="rr-th">Month</th>
                <th class="rr-th rr-th--num">Apportioned Fee</th>
                <th
                  v-for="category in categoryNames"
                  :key="category"
                  class="rr-th rr-th--num"
                >
                  {{ shortCategory(category) }}
                </th>
                <th class="rr-th rr-th--num">Net</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="month in schedule.months" :key="month.label">
                <td class="rr-td rr-td--strong">
                  {{ month.label }}
                  <span class="rr-sub">{{ month.financial_year }}</span>
                </td>
                <td class="rr-td rr-td--num">
                  {{ money(month.apportioned_fee) }}
                  <span class="rr-sub">{{ month.collection_count }} live</span>
                </td>
                <td
                  v-for="line in month.categories"
                  :key="line.key"
                  class="rr-td rr-td--num"
                >
                  {{ money(line.net_amount) }}
                </td>
                <td class="rr-td rr-td--num rr-td--strong">
                  {{ money(month.net_amount) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>

      <!-- Student detail -->
      <v-card v-else outlined class="rr-card">
        <div class="rr-card__head">
          <h3 class="rr-card__title">Student apportionment</h3>
          <v-text-field
            v-model="search"
            placeholder="Filter"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            class="rr-search"
          />
        </div>
        <v-data-table
          :headers="studentHeaders"
          :items="schedule.students"
          :search="search"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          dense
          class="rr-data-table"
        >
          <template #[`item.amount`]="{ item }">{{ money(item.amount) }}</template>
          <template #[`item.monthly_fee`]="{ item }">
            {{ money(item.monthly_fee) }}
          </template>
          <template #[`item.monthly_net`]="{ item }">
            {{ money(item.monthly_net) }}
          </template>
          <template #[`item.total_net`]="{ item }">
            {{ money(item.total_net) }}
          </template>
          <template #[`item.service_start`]="{ item }">
            {{ monthLabel(item.service_start) }} –
            {{ monthLabel(item.service_end) }}
          </template>
          <template #[`item.bucket_label`]="{ item }">
            <v-chip x-small label outlined>{{ item.bucket_label }}</v-chip>
          </template>
        </v-data-table>
      </v-card>
    </template>
  </div>
</template>

<script>
export default {
  data() {
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      partners: [],
      programBatches: [],
      schedule: null,
      loading: false,
      view: "months",
      search: "",
      fromMenu: false,
      toMenu: false,
      filters: {
        partner_id: null,
        batch: null,
        from_date: this.toIso(first),
        to_date: this.toIso(last),
      },
      studentHeaders: [
        { text: "USN", value: "usn_id" },
        { text: "Student", value: "student_name" },
        { text: "Programme", value: "programme" },
        { text: "Fee Type", value: "fee_type" },
        { text: "Payment Type", value: "payment_type" },
        { text: "Bucket", value: "bucket_label" },
        { text: "Service Period", value: "service_start", sortable: false },
        { text: "Months", value: "months", align: "end" },
        { text: "Collected", value: "amount", align: "end" },
        { text: "Fee / Month", value: "monthly_fee", align: "end" },
        { text: "Net / Month", value: "monthly_net", align: "end" },
        { text: "Total Net", value: "total_net", align: "end" },
      ],
    };
  },

  computed: {
    hasData() {
      return Boolean(this.schedule?.months?.length);
    },

    categoryNames() {
      return (this.schedule?.months?.[0]?.categories || []).map(
        (line) => line.name,
      );
    },

    batchItems() {
      const seen = new Map();

      this.programBatches.forEach((batch) => {
        const value = `${batch.year}-${batch.batch}`;
        if (seen.has(value)) return;
        seen.set(value, {
          value,
          label: `${batch.batch === 2 ? "Jul" : "Jan"} ${String(
            batch.year,
          ).slice(-2)} Batch`,
          year: batch.year,
          batch: batch.batch,
        });
      });

      return [
        { value: null, label: "All Batches" },
        ...[...seen.values()].sort(
          (a, b) => b.year - a.year || b.batch - a.batch,
        ),
      ];
    },

    selectedBatch() {
      return this.batchItems.find((item) => item.value === this.filters.batch);
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [
      { text: "Partner Revenue" },
      { text: "Revenue Recognition" },
    ]);
    this.initialise();
  },

  methods: {
    async initialise() {
      await Promise.all([this.getPartners(), this.getProgramBatches()]);
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("partners");
        this.partners = (res.data.data || []).filter(
          (partner) => partner.type !== "Sponsor",
        );
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      }
    },

    async getProgramBatches() {
      try {
        const res = await this.$axios.get("/program_batches");
        this.programBatches = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load batches");
      }
    },

    async loadSchedule() {
      if (!this.filters.partner_id) return;

      this.loading = true;
      try {
        const params = {
          partner_id: this.filters.partner_id,
          from_date: this.filters.from_date,
          to_date: this.filters.to_date,
        };

        if (this.selectedBatch?.year) {
          params.batch_year = this.selectedBatch.year;
          params.batch_no = this.selectedBatch.batch;
        }

        const res = await this.$axios.get("/revenue_recognition", { params });
        this.schedule = res.data.data;
      } catch (err) {
        this.schedule = null;
        this.notifyError(err, "Unable to build the recognition schedule");
      } finally {
        this.loading = false;
      }
    },

    downloadSchedule() {
      const header = [
        "Month",
        "Financial Year",
        "Apportioned Fee",
        ...this.categoryNames,
        "Gross",
        "GST",
        "Net",
      ];

      const rows = this.schedule.months.map((month) => [
        month.label,
        month.financial_year,
        month.apportioned_fee,
        ...month.categories.map((line) => line.net_amount),
        month.gross_amount,
        month.gst_amount,
        month.net_amount,
      ]);

      const csv = [header, ...rows]
        .map((row) =>
          row
            .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
            .join(","),
        )
        .join("\n");

      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      link.download = `revenue_recognition_${this.filters.from_date}_${this.filters.to_date}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },

    shortCategory(name) {
      return name
        .replace(" Service", "")
        .replace("Online Education Support", "Online Edu")
        .replace("Examination Support", "Exam")
        .replace("Academic Support", "Academic");
    },

    monthLabel(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-IN", {
        month: "short",
        year: "numeric",
      });
    },

    money(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    toIso(date) {
      const offset = date.getTimezoneOffset() * 60000;
      return new Date(date.getTime() - offset).toISOString().slice(0, 10);
    },

    notifyError(err, fallback) {
      this.$store.commit("showSnackbar", {
        message: err?.response?.data?.message || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.rr-page {
  --rr-line: #e4e7ec;
  --rr-ink: #101828;
  --rr-ink-2: #475467;
  --rr-ink-3: #98a2b3;
  padding: 10px 14px 40px;
  margin: 0 15px;
}

.rr-toolbar {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #fff;
  border-bottom: 1px solid var(--rr-line);
  margin: -10px -14px 0;
}

.rr-toolbar__inner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 14px;
}

.rr-field {
  max-width: 170px;
  flex: 0 1 170px;
}

.rr-field--wide {
  max-width: 220px;
  flex: 0 1 220px;
}

.rr-toggle ::v-deep .v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.rr-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
}

.rr-state--card {
  border-radius: 8px;
  background: #fff;
  margin-top: 12px;
}

.rr-state__title {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--rr-ink);
}

.rr-state__text {
  margin: 0;
  max-width: 620px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--rr-ink-2);
}

.rr-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.rr-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fff;
}

.rr-stat--accent {
  border-color: var(--v-primary-base, #00aff1);
}

.rr-stat__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--rr-ink-2);
}

.rr-stat__value {
  font-size: 20px;
  font-weight: 700;
  color: var(--rr-ink);
  font-variant-numeric: tabular-nums;
}

.rr-stat__meta {
  font-size: 11.5px;
  color: var(--rr-ink-3);
}

.rr-card {
  margin-top: 12px;
  border-radius: 8px;
  background: #fff;
}

.rr-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--rr-line);
}

.rr-card__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--rr-ink);
}

.rr-card__sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--rr-ink-2);
}

.rr-search {
  max-width: 220px;
}

.rr-table-wrap {
  overflow-x: auto;
}

.rr-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 820px;
}

.rr-th {
  padding: 8px 12px;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--rr-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--rr-line);
  white-space: nowrap;
}

.rr-th--num {
  text-align: right;
}

.rr-td {
  padding: 8px 12px;
  font-size: 12.5px;
  color: var(--rr-ink-2);
  border-bottom: 1px solid #f4f5f7;
  white-space: nowrap;
}

.rr-td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rr-td--strong {
  font-weight: 700;
  color: var(--rr-ink);
}

.rr-td--muted {
  color: var(--rr-ink-3);
}

.rr-sub {
  display: block;
  font-size: 11px;
  font-weight: 400;
  color: var(--rr-ink-3);
}

.rr-data-table ::v-deep th {
  white-space: nowrap;
}

@media (max-width: 600px) {
  .rr-page {
    margin: 0;
    padding: 10px 10px 40px;
  }

  .rr-toolbar {
    position: static;
    margin: -10px -10px 0;
  }

  .rr-field,
  .rr-field--wide {
    max-width: none;
    flex: 1 1 100%;
  }
}
</style>
